import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import WrapperPageSimple from '../../WrapperPage/present/WrapperPageSimple'
import getEvenOddByIndex from '../../../functions/getEvenOddByIndex'
import PageMetaMappingDetail from './PageMetaMappingDetail'
import TargetPickerModal from './TargetPickerModal'
import Translation from '../../Translation/cont/Translation'
import RequestOverlayContainer from '../../overlay/container/RequestOverlayContainer'
import SearchInput from '../../general/SearchInput'
import CinemaBadgeList from '../../general/CinemaBadgeList'
import sortCinemas from '../../../functions/sortCinemas'

const PROVIDER_KEY_TO_PROP = {
    Mars: 'pyMovies',
    Kinoheld: 'khMovies',
    Compeso: 'compesoMovies',
    DieselV2: 'dieselMovies',
    Cortec: 'cortecMovies',
    MarsX: 'marsxMovies',
    Vista: 'vistaMovies',
}

const getSources = (pk, movieMappings) => (movieMappings && movieMappings[pk] && movieMappings[pk].source) || []

export const resolveSourceEntry = (entry, providerMoviesProps, cinemas) => {
    const providerMovieKey = Object.keys(entry)[0]
    const providerMovieUuid = entry[providerMovieKey]
    const storePropName = PROVIDER_KEY_TO_PROP[providerMovieKey]
    const store = providerMoviesProps[storePropName]
    const providerMovie = store && store[providerMovieUuid]
    if (!providerMovie) return null
    const cinema = cinemas && cinemas[providerMovie.cinema]
    return {
        providerMovieKey,
        providerMovieUuid,
        cinemaId: providerMovie.cinema || null,
        cinemaShort: cinema ? cinema.nameShort : (providerMovie.cinema || '?'),
        filmId: providerMovie.id || providerMovie.filmID || '',
        title: providerMovie.title || '',
    }
}

export const getFallbackTitle = (pk, mapping, pmPreviewMovies) => {
    const previewPk = mapping && mapping.preview
    const previewMovie = previewPk && pmPreviewMovies && pmPreviewMovies[previewPk]
    if (previewMovie) return previewMovie.title || pk
    return pk
}

export const getCliMovieTitle = (pk, cliMovies) => {
    const cliMovie = pk && cliMovies && cliMovies[pk]
    return cliMovie ? (cliMovie.titleDisplay || cliMovie.title || null) : null
}

export const getMetaMovieTitle = (pk, movieMappings, providerMoviesProps, cinemas, pmPreviewMovies, cliMovies) => {
    const cliTitle = getCliMovieTitle(pk, cliMovies)
    if (cliTitle) return cliTitle
    const first = getSources(pk, movieMappings).map(e => resolveSourceEntry(e, providerMoviesProps, cinemas)).find(Boolean)
    if (first) return first.title
    return getFallbackTitle(pk, movieMappings && movieMappings[pk], pmPreviewMovies)
}

const PageMetaMappingPage = (props) => {
    const {
        movieMappings, cinemas, clientCinemaId, pmPreviewMovies, cliMovies,
        pyMovies, khMovies, compesoMovies, dieselMovies, cortecMovies, marsxMovies, vistaMovies,
        onMerge, onMove, onSplit, goBackPath,
    } = props

    const providerMoviesProps = { pyMovies, khMovies, compesoMovies, dieselMovies, cortecMovies, marsxMovies, vistaMovies }

    const location = useLocation()
    const initialPk = new URLSearchParams(location.search).get('pk')
    const [selectedPk, setSelectedPk] = useState(initialPk || null)
    const [searchQuery, setSearchQuery] = useState('')
    const [pendingAction, setPendingAction] = useState(null)
    // pendingAction shape:
    //   null
    //   { type: 'split', providerMovieKey, providerMovieUuid }
    //   { type: 'move',  providerMovieKey, providerMovieUuid }
    //   { type: 'merge' }

    const allMetaMoviePks = movieMappings ? Object.keys(movieMappings) : []
    const allCinemaIds = sortCinemas(clientCinemaId, cinemas) || []

    // Deselect if selected meta movie was removed from state
    useEffect(() => {
        if (selectedPk && movieMappings && !movieMappings[selectedPk]) {
            setSelectedPk(null)
        }
    }, [movieMappings, selectedPk])

    const resolvedByPk = {}
    allMetaMoviePks.forEach(pk => {
        resolvedByPk[pk] = getSources(pk, movieMappings).map(e => resolveSourceEntry(e, providerMoviesProps, cinemas)).filter(Boolean)
    })

    const getPreviewCinemaShort = (pk) => {
        const previewPk = movieMappings && movieMappings[pk] && movieMappings[pk].preview
        const previewMovie = previewPk && pmPreviewMovies && pmPreviewMovies[String(previewPk)]
        if (!previewMovie) return null
        const cinema = previewMovie.cinemaPk && cinemas && cinemas[previewMovie.cinemaPk]
        return cinema ? cinema.nameShort : null
    }

    const getListTitle = (pk) => {
        const cliTitle = getCliMovieTitle(pk, cliMovies)
        if (cliTitle) return cliTitle
        const first = resolvedByPk[pk] && resolvedByPk[pk][0]
        if (first) return first.title
        return getFallbackTitle(pk, movieMappings[pk], pmPreviewMovies)
    }

    const filteredPks = allMetaMoviePks.filter(pk => {
        if (!searchQuery) return true
        return getListTitle(pk).toLowerCase().includes(searchQuery.toLowerCase())
    }).sort((a, b) => getListTitle(a).localeCompare(getListTitle(b)))

    const handleSplit = (providerMovieKey, providerMovieUuid) => {
        setPendingAction({ type: 'split', providerMovieKey, providerMovieUuid })
    }

    const handleMove = (providerMovieKey, providerMovieUuid) => {
        setPendingAction({ type: 'move', providerMovieKey, providerMovieUuid })
    }

    const handleMerge = () => {
        setPendingAction({ type: 'merge' })
    }

    const handleModalConfirm = (destinationPk, sourcePk, keptTitle) => {
        if (!pendingAction) return
        if (pendingAction.type === 'move') {
            onMove(pendingAction.providerMovieKey, pendingAction.providerMovieUuid, selectedPk, destinationPk)
        } else if (pendingAction.type === 'merge') {
            onMerge(sourcePk !== undefined ? sourcePk : selectedPk, destinationPk, keptTitle)
        }
        setPendingAction(null)
    }

    const handleSplitConfirm = () => {
        if (!pendingAction || pendingAction.type !== 'split') return
        onSplit(pendingAction.providerMovieKey, pendingAction.providerMovieUuid, selectedPk)
        setPendingAction(null)
    }

    const showModal = pendingAction && (pendingAction.type === 'move' || pendingAction.type === 'merge')
    const showSplitConfirm = pendingAction && pendingAction.type === 'split'

    return (
        <WrapperPageSimple pageTitleTransId="route_meta_mapping" addClassName="meta-mapping-page" overlay={<RequestOverlayContainer />} closePagePath={goBackPath}>
            <div className="meta-mapping-inner">

                {/* search */}
                <div className="meta-mapping-search-bar">
                    <SearchInput
                        placeholderTransId="social_media_search_movie_placeholder"
                        value={searchQuery}
                        onChange={setSearchQuery}
                        width={380}
                    />
                </div>

                {/* split panel */}
                <div className="meta-mapping-split">

                    {/* list */}
                    <div className="meta-mapping-list-col">
                        <div className="meta-mapping-list-hdr text-light-uppercase">
                            <div><Translation id="preview_movies_col_film" /></div>
                            {allCinemaIds.length > 1 && <div><Translation id="meta_mapping_col_sources" /></div>}
                            <div></div>
                        </div>
                        <ul className="list-main meta-mapping-list">
                            {filteredPks.map((pk, index) => {
                                const mapping = movieMappings[pk]
                                const hasProvider = resolvedByPk[pk].length > 0
                                const hasTmdb = !!(mapping && mapping.tmdb)
                                const hasPreview = !!(mapping && mapping.preview)
                                return (
                                <li key={pk} className={'list-item ' + getEvenOddByIndex(index) + (selectedPk === pk ? ' active' : '')}>
                                    <div className="meta-mapping-list-row">
                                        <div>
                                            <div className="meta-mapping-list-title">
                                                <span>{getListTitle(pk)}</span>
                                                <div className="meta-mapping-data-pills">
                                                    {hasProvider && <span className="meta-mapping-pill meta-mapping-pill--provider text-small-light"><Translation id="source_ks" /></span>}
                                                    {hasTmdb && <span className="meta-mapping-pill meta-mapping-pill--tmdb text-small-light"><Translation id="tmdb_label" /></span>}
                                                    {hasPreview && <span className="meta-mapping-pill meta-mapping-pill--preview text-small-light"><Translation id="button_preview" /></span>}
                                                </div>
                                                {allCinemaIds.length > 1 && (
                                                    <div className="badge-wrap">
                                                        <CinemaBadgeList
                                                            resolvedCinemaIds={resolvedByPk[pk].map(r => r.cinemaId).filter(Boolean)}
                                                            allCinemaIds={allCinemaIds}
                                                            cinemas={cinemas}
                                                            fallbackShort={hasPreview && !hasProvider ? getPreviewCinemaShort(pk) : null}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            <button
                                                className={'tmdb-action-btn text-small-regular' + (selectedPk === pk ? ' active' : '')}
                                                onClick={() => setSelectedPk(pk)}
                                            >
                                                <Translation id="meta_mapping_open" />
                                            </button>
                                        </div>
                                    </div>
                                </li>
                                )
                            })}
                        </ul>
                    </div>

                    {/* detail */}
                    <div className="meta-mapping-detail-col">
                        {selectedPk && movieMappings && movieMappings[selectedPk] ? (
                            <PageMetaMappingDetail
                                metaMoviePk={selectedPk}
                                mapping={movieMappings[selectedPk]}
                                cinemas={cinemas}
                                providerMoviesProps={providerMoviesProps}
                                pmPreviewMovies={pmPreviewMovies}
                                cliMovies={cliMovies}
                                onSplit={handleSplit}
                                onMove={handleMove}
                                onMerge={handleMerge}
                                showSplitConfirm={showSplitConfirm}
                                onSplitConfirm={handleSplitConfirm}
                                onCancelAction={() => setPendingAction(null)}
                            />
                        ) : (
                            <div className="meta-mapping-detail-empty text-small-regular">
                                <Translation id="content_no_movie_selected" />
                            </div>
                        )}
                    </div>

                </div>

                {/* target picker modal */}
                {showModal && (
                    <TargetPickerModal
                        type={pendingAction.type}
                        movieMappings={movieMappings}
                        cinemas={cinemas}
                        providerMoviesProps={providerMoviesProps}
                        pmPreviewMovies={pmPreviewMovies}
                        cliMovies={cliMovies}
                        excludePk={selectedPk}
                        onConfirm={handleModalConfirm}
                        onCancel={() => setPendingAction(null)}
                    />
                )}

            </div>
        </WrapperPageSimple>
    )
}

export default PageMetaMappingPage
